var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { buildQueryString } from "../utils/buildQueryString";
import { buildFacetsApiUrl } from "./buildFacetsApiUrl";
let facetsAbortController = null;
let syncAbortController = null;
function startFacetsRequest() {
    if (facetsAbortController) {
        facetsAbortController.abort();
    }
    facetsAbortController = new AbortController();
    return facetsAbortController.signal;
}
function startSyncRequest() {
    if (syncAbortController) {
        syncAbortController.abort();
    }
    // A URL sync supersedes any in-flight facet refresh (e.g. from an open
    // popover): otherwise a late facets_success could overwrite the synced state
    // with counts for the previous selection.
    if (facetsAbortController) {
        facetsAbortController.abort();
        facetsAbortController = null;
    }
    syncAbortController = new AbortController();
    return syncAbortController.signal;
}
/**
 * The facets URL comes from a DOM attribute and the page query string, so it is
 * only ever requested when it resolves to the current origin.
 */
export function toSameOriginUrl(url) {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) {
        throw new Error("Facets request must target the current origin");
    }
    return `${parsed.pathname}${parsed.search}`;
}
function doFetch(url, getSignal, clearController) {
    return __awaiter(this, void 0, void 0, function* () {
        const signal = getSignal();
        let res;
        try {
            res = yield fetch(toSameOriginUrl(url), {
                method: "GET",
                headers: { Accept: "application/json" },
                signal,
            });
        }
        catch (err) {
            clearController();
            if (err instanceof DOMException && err.name === "AbortError") {
                return undefined;
            }
            throw err;
        }
        clearController();
        if (!res.ok) {
            throw new Error(`Facets request failed: ${res.status}`);
        }
        return res;
    });
}
/**
 * Fetch the full initial payload (filterConfig, facets, offersCount, etc.)
 * passing current page query params so active filters are reflected.
 */
export function fetchInitialPayload(facetsUrl_1) {
    return __awaiter(this, arguments, void 0, function* (facetsUrl, pathname = window.location.pathname, search = window.location.search, lockedFinanceType = null, hiddenFilters = {}) {
        const url = buildFacetsApiUrl(facetsUrl, pathname, search, lockedFinanceType, hiddenFilters);
        const res = yield doFetch(url, startSyncRequest, () => {
            syncAbortController = null;
        });
        if (!res)
            return undefined;
        return (yield res.json());
    });
}
/**
 * Fetch facets for current filter selection.
 * Any in-flight request is aborted before starting a new one.
 * Returns undefined if the request was aborted (caller should keep previous state).
 * Throws on real network/server errors.
 */
export function fetchFacets(baseUrl, selected) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const query = buildQueryString(selected);
        const url = `${baseUrl.replace(/\?.*$/, "")}${query}`;
        const res = yield doFetch(url, startFacetsRequest, () => {
            facetsAbortController = null;
        });
        if (!res)
            return undefined;
        const data = (yield res.json());
        return {
            facets: (_a = data.facets) !== null && _a !== void 0 ? _a : {},
            offersCount: (_b = data.offersCount) !== null && _b !== void 0 ? _b : 0,
        };
    });
}
